// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch the initial session
    const fetchSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Error fetching session:', error);
      } else {
        console.log('Initial session:', data.session);
        setSession(data.session);
        setUser(data.session?.user ?? null);
      }
      setLoading(false);
    };

    fetchSession();

    // Listen for authentication state changes
    const { data, error } = supabase.auth.onAuthStateChange((event, session) => {
      // console.log(`Auth event: ${event}`, session);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    if (error) {
      console.error('Error setting up auth state change subscription:', error);
    }

    // Cleanup the subscription on unmount
    if (data?.subscription) {
      return () => {
        console.log('Unsubscribing from auth state changes');
        data.subscription.unsubscribe();
      };
    }
  }, []);

  // Authentication functions
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error('Error during sign in:', error);
      throw error;
    }
    // console.log('Signed in:', data.session);
    setSession(data.session);
    setUser(data.session?.user ?? null);
  };

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) {
      console.error('Error during sign up:', error);
      throw error;
    }
    // console.log('Signed up:', data.session);
    setSession(data.session);
    setUser(data.session?.user ?? null);
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error during sign out:', error);
      throw error;
    }
    // console.log('Signed out');
    setSession(null);
    setUser(null);
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
